import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { useOrganizationId } from '../auth/hooks';
import { DatePickerInput } from '../components/DatePickerInput';
import { FormInput } from '../components/FormInput';
import { useToast } from '../components/ToastProvider';
import './FormPages.css';

type FormData = {
  carId: string;
  customerId: string;
  startDate: string;
  endDate: string;
  address: string;
  paymentMethod: 'cash' | 'credit_card' | 'debit_card' | 'bank_transfer';
  cardLastFour: string;
  deposit: string;
  dailyRate: string;
};

export default function RentalForm() {
  const { rentalId } = useParams<{ rentalId: string }>();
  const navigate = useNavigate();
  const { state, rentalOperations } = useAppContext();
  const organizationId = useOrganizationId();
  const isEditMode = rentalId !== undefined;
  const rental = isEditMode ? state.rentals.find(r => r.id === rentalId) : undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rentalDays, setRentalDays] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [amountDue, setAmountDue] = useState(0);
  const { showToast } = useToast();

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      carId: rental?.carId || '',
      customerId: rental?.customerId || '',
      startDate: rental?.startDate || '',
      endDate: rental?.endDate || '',
      address: rental?.address || '',
      paymentMethod: rental?.paymentMethod || 'credit_card',
      cardLastFour: rental?.cardLastFour || '',
      deposit: rental?.deposit?.toString() || '200',
      dailyRate: rental?.dailyRate?.toString() || '50',
    },
    mode: 'onChange'
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const dailyRate = watch('dailyRate');
  const deposit = watch('deposit');

  useEffect(() => {
    if (rental) {
      setValue('carId', rental.carId);
      setValue('customerId', rental.customerId);
      setValue('startDate', rental.startDate);
      setValue('endDate', rental.endDate || '');
      setValue('address', rental.address || '');
      setValue('paymentMethod', rental.paymentMethod || 'credit_card');
      setValue('cardLastFour', rental.cardLastFour || '');
      setValue('deposit', rental.deposit?.toString() || '200');
      setValue('dailyRate', rental.dailyRate?.toString() || '50');
    }
  }, [rental, setValue]);

  // Calculate rental duration and costs
  useEffect(() => {
    if (startDate && endDate && dailyRate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
      
      setRentalDays(diffDays);
      
      const daily = parseFloat(dailyRate) || 0;
      const depositAmount = parseFloat(deposit) || 0;
      const total = diffDays * daily;
      const due = total + depositAmount;
      
      setTotalCost(total);
      setAmountDue(due);
    } else {
      setRentalDays(0);
      setTotalCost(0);
      setAmountDue(parseFloat(deposit) || 0);
    }
  }, [startDate, endDate, dailyRate, deposit]);

  const onSubmit = async (data: FormData) => {
    if (!organizationId) {
      alert('Organization ID is required');
      return;
    }

    if (!data.carId || !data.customerId || !data.startDate || !data.address) {
      alert('Please fill out all required fields');
      return;
    }

    const start = new Date(data.startDate);
    const end = data.endDate ? new Date(data.endDate) : null;
    if (end && !isNaN(end.getTime()) && end < start) {
      alert('End date cannot be before start date');
      return;
    }

    setIsSubmitting(true);
    try {
      const rentalData = {
        organizationId,
        carId: data.carId,
        customerId: data.customerId,
        startDate: data.startDate,
        endDate: data.endDate || undefined,
        address: data.address,
        paymentMethod: data.paymentMethod,
        cardLastFour: data.cardLastFour || undefined,
        deposit: parseFloat(data.deposit) || 0,
        dailyRate: parseFloat(data.dailyRate) || 0,
        totalCost,
        amountDue,
      };

      if (isEditMode && rentalId) {
        await rentalOperations.update(rentalId, rentalData);
      } else {
        await rentalOperations.add(rentalData);
      }
      showToast({ message: isEditMode ? 'Rental updated successfully.' : 'Rental created successfully.' });
      navigate('/rentals');
    } catch (err) {
      showToast({ message: 'Failed to save rental: ' + (err as Error).message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableCars = state.cars.filter(car => car.status === 'available');

  return (
    <div className="form-page">
      <div className="form-page-header">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/rentals')}>← Back</button>
        <h1 className="form-page-title">{isEditMode ? 'Edit Rental' : 'New Rental Agreement'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="form-card">
        <div className="form-stack">
          <Controller
            control={control}
            name="carId"
            rules={{ required: 'Car is required' }}
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Select Vehicle *
                </label>
                <select
                  className="form-select"
                  value={field.value}
                  onChange={field.onChange}
                >
                  <option value="">-- Select a Car --</option>
                  {availableCars.map(car => (
                    <option key={car.id} value={car.id}>
                      {car.year} {car.make} {car.model} ({car.licensePlate})
                    </option>
                  ))}
                </select>
                {errors.carId && <p className="form-error">{errors.carId.message}</p>}
              </div>
            )}
          />

          <Controller
            control={control}
            name="customerId"
            rules={{ required: 'Customer is required' }}
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Select Customer *
                </label>
                <select
                  className="form-select"
                  value={field.value}
                  onChange={field.onChange}
                >
                  <option value="">-- Select a Customer --</option>
                  {state.customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} - {customer.email || customer.phone ? `${customer.email || ''}${customer.email && customer.phone ? ' | ' : ''}${customer.phone || ''}` : customer.contactInfo}
                    </option>
                  ))}
                </select>
                {errors.customerId && <p className="form-error">{errors.customerId.message}</p>}
              </div>
            )}
          />

          <Controller
            control={control}
            name="startDate"
            rules={{ required: 'Start date is required' }}
            render={({ field }) => (
              <DatePickerInput
                label="Start Date"
                value={field.value}
                onChange={field.onChange}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="endDate"
            render={({ field }) => (
              <DatePickerInput
                label="End Date (optional)"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            control={control}
            name="address"
            rules={{ required: 'Address is required' }}
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Customer Address *
                </label>
                <textarea
                  className="form-textarea form-textarea-sm"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Enter customer address"
                />
                {errors.address && <p className="form-error">{errors.address.message}</p>}
              </div>
            )}
          />

          <Controller
            control={control}
            name="paymentMethod"
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Payment Method
                </label>
                <select
                  className="form-select"
                  value={field.value}
                  onChange={field.onChange}
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
            )}
          />

          <Controller
            control={control}
            name="cardLastFour"
            render={({ field }) => {
              const paymentMethod = watch('paymentMethod');
              if (paymentMethod === 'cash') return <></>;
              return (
                <FormInput
                  label="Card Last 4 Digits (optional)"
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  maxLength={4}
                  placeholder="0000"
                />
              );
            }}
          />

          <Controller
            control={control}
            name="dailyRate"
            rules={{ required: 'Daily rate is required' }}
            render={({ field }) => (
              <FormInput
                label="Daily Rate ($)"
                type="number"
                value={field.value}
                onChange={field.onChange}
                error={errors.dailyRate?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="deposit"
            rules={{ required: 'Deposit is required' }}
            render={({ field }) => (
              <FormInput
                label="Security Deposit ($)"
                type="number"
                value={field.value}
                onChange={field.onChange}
                error={errors.deposit?.message}
                required
              />
            )}
          />

          {rentalDays > 0 && (
            <div className="form-summary">
              <h3>Rental Calculation</h3>
              <p>Duration: {rentalDays} day{rentalDays > 1 ? 's' : ''}</p>
              <p>Daily Rate: ${parseFloat(dailyRate || '0').toFixed(2)}</p>
              <p>Subtotal: ${totalCost.toFixed(2)}</p>
            </div>
          )}

          <div className="form-totals">
            <h3>Payment Summary</h3>
            <div className="form-total-row">
              <span>Rental Cost ({rentalDays || 0} days):</span>
              <span>${totalCost.toFixed(2)}</span>
            </div>
            <div className="form-total-row">
              <span>Security Deposit:</span>
              <span>${parseFloat(deposit || '0').toFixed(2)}</span>
            </div>
            <div className="form-total-row form-total-row-strong">
              <span>Total Amount Due:</span>
              <span className="form-total-accent">${amountDue.toFixed(2)}</span>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Rental' : 'Create Rental Agreement'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/rentals')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

